<template>
  <slot v-if="isEnabled">
    <div class="absolute top-2 right-2 z-50">
      <button
        @click="handleExplain"
        :disabled="loading"
        class="p-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 group"
        :class="{
          'opacity-50 cursor-not-allowed': loading,
          'hover:bg-blue-50': !loading,
        }"
        title="Explain this chart to me">
        <LightbulbIconAnimated
          v-if="!loading"
          class="w-5 h-5 transition-all duration-200 group-hover:scale-110" />
        <BeatLoader v-else color="#0064FF" :size="8" class="w-5 h-5" />
      </button>
    </div>
  </slot>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { Chart } from 'chart.js'
import LightbulbIconAnimated from '@/components/icons/LightbulbIconAnimated.vue'
import BeatLoader from 'vue-spinner/src/PulseLoader.vue'
import { useChartExplanation } from '@/composables/charts/useChartExplanation'
import { useGlobalChat } from '@/composables/useGlobalChat'
import type { ChartContext } from '@/types/chartExplanation'
import { useLDFlag } from '@/lib/ldClient.ts'
import { FeatureList } from '@/lib/ldClient.ts'

const isEnabled = useLDFlag(FeatureList.ENABLE_EXPLAIN_THIS_CHART_TO_ME)

interface Props {
  chart: Chart
  context?: ChartContext
  disabled?: boolean
}

interface Emits {
  (e: 'error', error: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const loading = ref(false)
const { extractChartData } = useChartExplanation()
const { triggerChatWithChart } = useGlobalChat()

const handleExplain = async () => {
  if (loading.value || props.disabled) return

  try {
    loading.value = true

    // Extract chart data including tooltips
    const chartData = await extractChartData(props.chart, {
      includeTooltips: true,
      includeRawData: true,
      context: props.context,
      waitForAnimation: true,
    })

    // Trigger global chat with chart data
    triggerChatWithChart(chartData)
  } catch (error) {
    console.error('Error explaining chart:', error)
    emit('error', error instanceof Error ? error.message : 'Failed to explain chart')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* Styles can be added here if needed */
</style>
